import { Injectable } from '@angular/core';
import {
    BilledProcedureGridObject,
    BilledProcedureUpdateInput,
    BilledVisitGridObject,
    BilledVisitUpdateInput,
    BillingBatchGridObject,
    BillingBatchObject,
    BillingDocumentGridObject,
    BillingDocumentObject,
    BillingDocumentRefuseInput,
    BillingDocumentRenumberInput,
    BillingErrorsObject,
    BillingExportInput,
    BillingFilesObject,
    BillingGridObject,
    BillingObject,
    CreateBillingInput,
    DeleteBillingInput,
    DeleteInvoiceInput,
    FileDownloadObject,
    GetBillingBatchInput,
    GetBillingDocumentInput,
    GetBillingErrorsInput,
    GetInvoiceInput,
    InvoiceGridObject,
    InvoiceObject,
    OkObject,
    PrepareBillingInput,
    UpdateBillingBatchInput,
    UpdateBillingDocumentInput,
    UpdateInvoiceInput,
} from '@it2go/types';
import { billingFilesFrag, billingFrag } from './types/billing.types';
import { GridInput } from '@it2go/data-grid';
import { filterFrag } from '../modules/content/service/types/filter.types';
import { GqlQueryOptions, GqlServiceV2 } from '../modules/content/service/gql.service-v2';
import {
    BilledProcedureCheckWorkChangeInput,
} from '@libs/types/src/services/input-type/billing/billed-procedure.check-work-change-input';
import {
    BilledVisitCheckWorkChangeInput,
} from '@libs/types/src/services/input-type/billing/billed-visit.check-work-change-input';
import { WorkChangedObject } from '@libs/types/src/services/object-type/billing/work-changed.object';

@Injectable({ providedIn: 'root' })
export class BillingGqlServiceV2 extends GqlServiceV2 {

    getBillingErrors(input: GetBillingErrorsInput, frag: string, options: GqlQueryOptions = {})  {
        return this.query<BillingErrorsObject>(
            `
                query($input: GetBillingErrorsInput!) {
                    billing {
                        listErrors(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
            options,
        );
    }

    getBillings(input: GridInput | null, frag: string) {
        return this.query<BillingGridObject>(
            `
                query($input: GridInput) {
                    billing {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    createBilling(input: CreateBillingInput, frag: string = billingFilesFrag) {
        return this.mutate<BillingFilesObject>(
            `
                mutation($input: CreateBillingInput!) {
                    billing {
                        create(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    prepareBilling(input: PrepareBillingInput, frag: string = billingFrag) {
        return this.mutate<BillingObject>(
            `
                mutation($input: PrepareBillingInput!) {
                    billing {
                        prepare(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    deleteBilling(input: DeleteBillingInput) {
        return this.mutate<OkObject>(
            `
                mutation ($input: DeleteBillingInput!) {
                    billing {
                        delete(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        )
    }

    checkUniqueInvoiceNumber(input: GridInput | null, frag: string) {
        return this.getInvoices(input, frag);
    }

    getInvoices(input: GridInput | null, frag: string) {
        return this.query<InvoiceGridObject>(
            `
                query($input: GridInput) {
                    invoice {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    getInvoice(input: GetInvoiceInput, frag: string) {
        return this.query<InvoiceObject>(
            `
                query($input: GetInvoiceInput!) {
                    invoice {
                        get(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        )
    }

    updateInvoice(input: UpdateInvoiceInput, frag: string) {
        return this.mutate<InvoiceObject>(`
                mutation ($input: UpdateInvoiceInput!) {
                    invoice {
                        update(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    deleteInvoice(input: DeleteInvoiceInput) {
        return this.mutate<OkObject>(
            `
                mutation ($input: DeleteInvoiceInput!) {
                    invoice {
                        delete(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    getBillingBatchesOfVoucherType(input: GridInput | null, frag: string) {
        return this.getBillingBatches(input, frag);
    }

    getBillingBatchesOfAmbulantType(input: GridInput | null, frag: string) {
        return this.getBillingBatches(input, frag);
    }

    getBillingBatchesOfTransportType(input: GridInput | null, frag: string) {
        return this.getBillingBatches(input, frag);
    }

    checkUniqueBillingBatchNumber(input: GridInput | null, frag: string) {
        return this.getBillingBatches(input, frag);
    }

    getBillingBatches(input: GridInput | null, frag: string) {
        return this.query<BillingBatchGridObject>(
            `
                query($input: GridInput) {
                    billingBatch {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    getBillingBatch(input: GetBillingBatchInput, frag: string) {
        return this.query<BillingBatchObject>(
            `
                query($input: GetBillingBatchInput!) {
                    billingBatch {
                        get(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        )
    }

    updateBillingBatch(input: UpdateBillingBatchInput, frag: string) {
        return this.mutate<BillingBatchObject>(`
                mutation ($input: UpdateBillingBatchInput!) {
                    billingBatch {
                        update(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    renumberBillingDocuments(input: BillingDocumentRenumberInput) {
        return this.mutate<OkObject>(`
                mutation ($input: BillingDocumentRenumberInput!) {
                    billingBatch {
                        renumberBillingDocuments(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    getBillingDocuments(input: GridInput | null, frag: string) {
        return this.query<BillingDocumentGridObject>(
            `
                query($input: GridInput) {
                    billingDocument {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    getBillingDocument(input: GetBillingDocumentInput, frag: string) {
        return this.query<BillingDocumentObject>(
            `
                query($input: GetBillingDocumentInput!) {
                    billingDocument {
                        get(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        )
    }

    updateBillingDocument(input: UpdateBillingDocumentInput, frag: string) {
        return this.mutate<BillingDocumentObject>(`
                mutation ($input: UpdateBillingDocumentInput!) {
                    billingDocument {
                        update(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    refuseBillingDocuments(input: BillingDocumentRefuseInput[]) {
        return this.mutate<OkObject>(`
                mutation ($input: [BillingDocumentRefuseInput!]!) {
                    billingDocument {
                        refuse(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    getBilledProcedures(input: GridInput | null, frag: string) {
        return this.query<BilledProcedureGridObject>(
            `
                query($input: GridInput) {
                    billedProcedure {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public updateBilledProcedures(input: BilledProcedureUpdateInput[]) {
        return this.mutate(
            `
            mutation($input: [BilledProcedureUpdateInput!]!) {
                billedProcedure {
                    updateMany(input: $input) {
                        ok
                    }
                }
            }
        `,
            { input },
        );
    }

    public billedProcedureCheckIfWorkHasChanged(input: BilledProcedureCheckWorkChangeInput): Promise<WorkChangedObject> {
        return this.mutate(
            `
                mutation($input: BilledProcedureCheckWorkChangeInput!) {
                    billedProcedure {
                        checkIfWorkHasChanged(input: $input) {
                            changed
                        }
                    }
                }
            `,
            { input },
        );
    }

    public billedProcedureRevertChangedWork(input: BilledProcedureCheckWorkChangeInput): Promise<OkObject> {
        return this.mutate(
            `
                mutation($input: BilledProcedureCheckWorkChangeInput!) {
                    billedProcedure {
                        revertChangedWork(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    getBilledVisits(input: GridInput | null, frag: string) {
        return this.query<BilledVisitGridObject>(
            `
                query($input: GridInput) {
                    billedVisit {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    getPreviouslyBilledVisits(input: GridInput | null, frag: string) {
        return this.getBilledVisits(input, frag);
    }

    public updateBilledVisits(input: BilledVisitUpdateInput[]) {
        return this.mutate(
            `
            mutation($input: [BilledVisitUpdateInput!]!) {
                billedVisit {
                    updateMany(input: $input) {
                        ok
                    }
                }
            }
        `,
            { input },
        );
    }

    public billedVisitCheckIfWorkHasChanged(input: BilledVisitCheckWorkChangeInput): Promise<WorkChangedObject> {
        return this.mutate(
            `
                mutation($input: BilledVisitCheckWorkChangeInput!) {
                    billedVisit {
                        checkIfWorkHasChanged(input: $input) {
                            changed
                        }
                    }
                }
            `,
            { input },
        );
    }

    public billedVisitRevertChangedWork(input: BilledVisitCheckWorkChangeInput): Promise<OkObject> {
        return this.mutate(
            `
                mutation($input: BilledVisitCheckWorkChangeInput!) {
                    billedVisit {
                        revertChangedWork(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public exportToPdf(input: BillingExportInput): Promise<FileDownloadObject> {
        return this.mutate(
            `
                mutation($input: BillingExportInput!) {
                    billing {
                        exportToPdf(input: $input) {
                            fileName
                            content
                        }
                    }
                }
            `,
            { input },
        );
    }

    public exportToIsdoc(input: BillingExportInput): Promise<FileDownloadObject> {
        return this.mutate(
            `
                mutation($input: BillingExportInput!) {
                    billing {
                        exportToIsdoc(input: $input) {
                            fileName
                            content
                        }
                    }
                }
            `,
            { input },
        );
    }

    public exportToPohoda(input: BillingExportInput): Promise<FileDownloadObject> {
        return this.mutate(
            `
                mutation($input: BillingExportInput!) {
                    billing {
                        exportToPohoda(input: $input) {
                            fileName
                            content
                        }
                    }
                }
            `,
            { input },
        );
    }
}
